// <pos-login/> — 登入
//   App（Capacitor 原生殼）：Sign in with Apple + Google 原生按鈕
//   Web（PWA）：email 密碼 + Magic Link
// see architecture.md#L280 (auth API) + src/lib/native-auth.js

import { getSupabase } from '../lib/supabase-client.js';
import { isNativeApp, platform, signInWithApple, signInWithGoogle } from '../lib/native-auth.js';

class PosLogin extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.native = isNativeApp();
    this.platform = platform();   // 'ios' | 'android' | 'web'
  }

  connectedCallback() {
    this.render();
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host { display: flex; align-items: center; justify-content: center; min-height: 100vh;
          padding: env(safe-area-inset-top,0) env(safe-area-inset-right,0) env(safe-area-inset-bottom,0) env(safe-area-inset-left,0); box-sizing: border-box; }
        .card {
          max-width: 400px; width: 90%; padding: var(--s-8);
          background: var(--bg-elevated); border: 1px solid var(--border);
          border-radius: var(--r-lg); box-shadow: var(--shadow-lg);
        }
        h1 { font-family: var(--font-display); color: var(--brand-primary); font-size: 2rem; font-weight: 700; margin: 0 0 var(--s-1); letter-spacing: -0.02em; }
        .tagline { color: var(--fg-muted); font-size: .85rem; margin: 0 0 var(--s-6); }
        p { color: var(--fg-muted); margin-bottom: var(--s-4); font-size: .9rem; }
        input { width: 100%; margin-bottom: var(--s-3); }
        .tabs { display: flex; gap: var(--s-2); margin-bottom: var(--s-4); border-bottom: 1px solid var(--border); }
        .tab { flex: 1; padding: var(--s-2) 0; background: none; border: 0; color: var(--fg-muted); cursor: pointer; font-size: .95rem; border-bottom: 2px solid transparent; }
        .tab.is-active { color: var(--brand-primary); border-bottom-color: var(--brand-primary); }
        .msg { color: var(--success); margin-top: var(--s-4); font-size: .85rem; }
        .err { color: var(--danger); margin-top: var(--s-4); font-size: .85rem; }
        form { display: none; }
        form.is-active { display: block; }
        /* 原生登入按鈕 */
        .sso { display: flex; flex-direction: column; gap: var(--s-3); }
        .sso-btn { display: flex; align-items: center; justify-content: center; gap: var(--s-2);
          width: 100%; padding: var(--s-4); border-radius: var(--r-md); font-size: 1rem; font-weight: 600;
          cursor: pointer; border: 1px solid transparent; font-family: inherit; }
        .sso-apple { background: #000; color: #fff; }
        .sso-google { background: #fff; color: #1f1f1f; border-color: #dadce0; }
        .sso-btn svg { width: 20px; height: 20px; }
        .or { display: flex; align-items: center; gap: var(--s-3); margin: var(--s-5) 0; color: var(--fg-dim); font-size: .8rem; }
        .or::before, .or::after { content: ''; flex: 1; height: 1px; background: var(--border); }
        /* Email 為主要登入方式 — 大而明顯好按 */
        .email-form { display: block; margin-top: var(--s-2); }
        .email-form label { display: block; font-size: .82rem; font-weight: 600; color: var(--fg-muted); margin-bottom: 6px; }
        .email-form input { width: 100%; box-sizing: border-box; padding: 15px var(--s-4); font-size: 1.05rem;
          margin-bottom: var(--s-4); border: 1px solid var(--border); border-radius: var(--r-md);
          background: var(--bg-surface); color: var(--fg-base); }
        .email-form input:focus { outline: none; border-color: var(--brand-primary); }
        .btn-login { width: 100%; padding: 16px; font-size: 1.15rem; font-weight: 700; font-family: inherit;
          background: var(--brand-primary); color: #fff; border: 0; border-radius: var(--r-md);
          cursor: pointer; margin-top: var(--s-2); }
        .btn-login:active { opacity: .85; }
        .btn-signup { width: 100%; padding: 13px; font-size: .95rem; font-family: inherit; margin-top: var(--s-3);
          background: transparent; border: 1px solid var(--border); color: var(--fg-base);
          border-radius: var(--r-md); cursor: pointer; }
        #msg-email { margin-top: var(--s-3); font-size: .9rem; line-height: 1.5; }
        #msg-email.msg { color: var(--success); }
        #msg-email.err { color: var(--danger); }
        .sso-hint { color: var(--fg-dim); font-size: .8rem; text-align: center; margin-top: var(--s-4); }
      </style>
      <div class="card">
        <h1>TAUKE POS</h1>
        <p class="tagline">一杯咖啡的價格，AI 幫你顧生意</p>
        ${this.native ? this.renderNative() : this.renderWeb()}
        <div id="sso-msg"></div>
      </div>
    `;

    if (this.native) {
      this.shadowRoot.querySelector('#btn-apple')?.addEventListener('click', () => this.onApple());
      this.shadowRoot.querySelector('#btn-google')?.addEventListener('click', () => this.onGoogle());
      const ef = this.shadowRoot.querySelector('#f-email');
      if (ef) {
        ef.addEventListener('submit', (e) => { e.preventDefault(); this.onEmailAuth('login'); });
        ef.querySelector('[data-mode="signup"]')?.addEventListener('click', () => this.onEmailAuth('signup'));
      }
      return;
    }
    {
      this.shadowRoot.querySelectorAll('.tab').forEach(t =>
        t.addEventListener('click', () => this.switchTab(t.dataset.tab)));
      this.shadowRoot.querySelector('#f-password').addEventListener('submit', (e) => this.onPassword(e));
      this.shadowRoot.querySelector('#f-magic').addEventListener('submit', (e) => this.onMagic(e));
    }
  }

  renderNative() {
    // Email 為主要登入方式（自家帳號、非第三方 SSO → 不觸發 Apple 4.8）。
    // iOS：Apple 登入 2026-07-14 重新開啟 — 07-07/08 的 error 1001 根因是
    //   capability 啟用後 Apple 後端傳播窗（DTS thread 789697：最長 24h），
    //   當時測試落在窗內；capability 已由 ASC API 確認生效。
    //   若再失敗，畫面紅字有完整 NSError 鏈（AppleSignInPlugin.swift）可診斷。
    // Android：保留 Google 登入（Play 生態、無 4.8 限制）。
    const appleBtn = `
        <button id="btn-apple" class="sso-btn sso-apple">
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8.94-.19 1.84-.86 3.28-.76 1.72.14 3.02.82 3.88 2.05-3.55 2.13-2.99 6.81.32 8.14-.65 1.71-1.49 2.32-2.56 2.74zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
          使用 Apple 登入
        </button>`;
    const googleBtn = `
        <button id="btn-google" class="sso-btn sso-google">
          <svg viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          使用 Google 登入
        </button>`;
    const ssoBtn = this.platform === 'android' ? googleBtn : this.platform === 'ios' ? appleBtn : '';
    return `
      <form id="f-email" class="email-form">
        <label>Email</label>
        <input type="email" name="email" placeholder="your@email.com" required autocomplete="username" inputmode="email" autocapitalize="off" />
        <label>密碼</label>
        <input type="password" name="password" placeholder="至少 6 位" required minlength="6" autocomplete="current-password" />
        <button type="submit" class="btn-login" data-mode="login">登入</button>
        <button type="button" class="btn-signup" data-mode="signup">第一次使用？註冊新帳號</button>
        <div id="msg-email"></div>
      </form>
      ${ssoBtn ? `<div class="or"><span>或</span></div><div class="sso">${ssoBtn}</div>` : ''}
      <p class="sso-hint">首次註冊會自動建立帳號。登入即表示同意<a href="https://stella-pos.netlify.app/terms.html">服務條款</a>與<a href="https://stella-pos.netlify.app/privacy.html">隱私權政策</a>。</p>
    `;
  }

  renderWeb() {
    return `
      <div class="tabs">
        <button class="tab is-active" data-tab="password">密碼登入</button>
        <button class="tab" data-tab="magic">Magic Link</button>
      </div>
      <form id="f-password" class="is-active">
        <p>輸入 email 和密碼登入（推薦）。</p>
        <input type="email" name="email" placeholder="your@email.com" required autocomplete="username" />
        <input type="password" name="password" placeholder="密碼（≥ 8 字）" required minlength="8" autocomplete="current-password" />
        <button type="submit" class="btn btn-primary">登入</button>
        <div id="msg-password"></div>
      </form>
      <form id="f-magic">
        <p>輸入 email，我們會寄一封登入連結給你（每小時 4 封）。</p>
        <input type="email" name="email" placeholder="your@email.com" required autocomplete="username" />
        <button type="submit" class="btn btn-primary">寄送登入連結</button>
        <div id="msg-magic"></div>
      </form>
    `;
  }

  async onApple() { await this.doNative(signInWithApple, 'Apple'); }
  async onGoogle() { await this.doNative(signInWithGoogle, 'Google'); }

  async doNative(fn, label) {
    if (this._ssoBusy) return;                    // 防情境 2：授權進行中不重複觸發
    this._ssoBusy = true;
    const btnApple = this.shadowRoot.querySelector('#btn-apple');
    const btnGoogle = this.shadowRoot.querySelector('#btn-google');
    [btnApple, btnGoogle].forEach(b => { if (b) b.disabled = true; });
    const msg = this.shadowRoot.querySelector('#sso-msg');
    msg.className = ''; msg.textContent = `${label} 登入中…`;
    try {
      await fn();
      msg.className = 'msg'; msg.textContent = '✓ 登入成功，跳轉中…';
      setTimeout(() => { location.hash = '#/dashboard'; }, 200);
    } catch (err) {
      // 使用者取消不報錯
      if (/cancel/i.test(err?.message || '')) { msg.textContent = ''; return; }
      msg.className = 'err'; msg.textContent = `✗ ${err?.message || `${label} 登入失敗`}`;
    } finally {
      this._ssoBusy = false;
      [btnApple, btnGoogle].forEach(b => { if (b) b.disabled = false; });
    }
  }

  /** email 註冊/登入（native fallback）。login=密碼登入；signup=註冊新帳號 */
  async onEmailAuth(mode) {
    const root = this.shadowRoot;
    const email = root.querySelector('#f-email [name=email]').value.trim();
    const password = root.querySelector('#f-email [name=password]').value;
    const msg = root.querySelector('#msg-email');
    if (!email || password.length < 6) {
      msg.className = 'err'; msg.textContent = '請填 email 與至少 6 位密碼'; return;
    }
    msg.className = ''; msg.textContent = mode === 'signup' ? '註冊中…' : '登入中…';
    const supabase = getSupabase();
    try {
      if (mode === 'signup') {
        const { data, error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
        if (!data.session) {
          // confirm email 開啟 → 需驗證信
          msg.className = 'msg';
          msg.textContent = '✓ 註冊成功！我們寄了驗證信到你的信箱，點信裡的連結後就能登入。';
          return;
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
          // 帳號不存在時提示改用註冊
          if (/invalid login credentials/i.test(error.message)) {
            msg.className = 'err';
            msg.textContent = '登入失敗：帳號或密碼錯誤。第一次使用請按「註冊新帳號」。';
            return;
          }
          throw error;
        }
      }
      msg.className = 'msg'; msg.textContent = '✓ 成功，跳轉中…';
      setTimeout(() => { location.hash = '#/dashboard'; }, 200);
    } catch (err) {
      msg.className = 'err'; msg.textContent = `✗ ${err.message_zh || err.message || '失敗'}`;
    }
  }

  switchTab(which) {
    this.shadowRoot.querySelectorAll('.tab').forEach(t => t.classList.toggle('is-active', t.dataset.tab === which));
    this.shadowRoot.querySelectorAll('form').forEach(f => f.classList.toggle('is-active', f.id === `f-${which}`));
  }

  async onPassword(e) {
    e.preventDefault();
    const email = e.target.email.value.trim();
    const password = e.target.password.value;
    const msg = this.shadowRoot.querySelector('#msg-password');
    msg.className = ''; msg.textContent = '登入中...';
    try {
      const { error } = await getSupabase().auth.signInWithPassword({ email, password });
      if (error) throw error;
      msg.className = 'msg'; msg.textContent = '✓ 登入成功，跳轉中…';
      setTimeout(() => { location.hash = '#/dashboard'; }, 200);
    } catch (err) {
      msg.className = 'err'; msg.textContent = `✗ ${err.message || '登入失敗'}`;
    }
  }

  async onMagic(e) {
    e.preventDefault();
    const email = e.target.email.value.trim();
    const msg = this.shadowRoot.querySelector('#msg-magic');
    msg.className = ''; msg.textContent = '寄送中...';
    try {
      const { error } = await getSupabase().auth.signInWithOtp({
        email, options: { emailRedirectTo: `${location.origin}/` },
      });
      if (error) throw error;
      msg.className = 'msg'; msg.textContent = '✓ 登入連結已寄出，請至信箱收信。';
    } catch (err) {
      msg.className = 'err'; msg.textContent = `✗ ${err.message || '寄送失敗'}`;
    }
  }
}

customElements.define('pos-login', PosLogin);
